import { useEffect, useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useRegistrationCampaign } from '@hotjar/webapp/hooks/useCampaigns';
import { MARKS } from '@hotjar/webapp/performance';
import { useAnonymousId } from '@hotjar/webapp/hooks/useAnonymousId';
import { verifyEmail } from '@hotjar/webapp/domains/common/verify-email/api';
import { useUserLogin } from '@hotjar/auth/src/domains/authentication/useUserLogin';
import type { VerifyEmailSuccessResponseDto } from '@hotjar/webapp/domains/common/verify-email/model';
import { clearParameter } from '@hotjar/insights-common/lib/url';
import { mark, measureAndLog } from '@hotjar/performance';
import { clearUserData } from '@hotjar/webapp/domains/common/user-data';
import type {
  SignupFormData,
  SubmitUserFormData,
} from '@hotjar/webapp/features/Registration/steps/SelectSignUpMethodStep/SelectSignUpMethodStep';
import { createUser } from '@hotjar/webapp/domains/acquisition/register/api';
import {
  DEFAULT_PLAN,
  DEFAULT_PRICING_TABLE_NUMBER,
  DEFAULT_SSO_PROVIDER,
} from '@hotjar/webapp/features/Registration/common/constants';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { ONBOARDING } from '@hotjar/webapp/constants/routes/onboarding';
import type { SignupMethodSettings } from './useRegisterQueryParams';
import { usePopulateStateParams, useRegisterQueryParams } from './useRegisterQueryParams';

interface RegistrationData {
  isFormLoaded: boolean;
  isCampaignValid: boolean;
  vidValue: string;
  userDetails: SignupFormData;
  resetUserDetailsForm: () => void;
  selectEmailMethod: () => void;
  shouldShowEmailForm: boolean;
  isEmailVerificationPending: boolean;
  onFormSubmit: SubmitUserFormData;
  isStepOneSubmitInProgress: boolean;
}

// eslint-disable-next-line func-params-args/func-params
export const useUserCreationForm = (
  trackOnEmailFormSubmit: () => void,
  trackOnDefaultSSOSubmit: () => void,
  signupMethod: SignupMethodSettings,
  captchaValue: string | null,
  isCaptchaVisible: boolean,
): RegistrationData => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const [shouldShowEmailForm, setShouldShowEmailForm] = useState(true);
  const [isCampaignValid, setIsCampaignValid] = useState(true);
  const [isEmailVerificationPending, setIsEmailVerificationPending] = useState(false);
  const [isStepOneSubmitInProgress, setStepOneSubmitInProgress] = useState(false);
  const [vidValue, setVidValue] = useState('');
  const stateParamsObject = usePopulateStateParams({ signupMethod: signupMethod.method });

  const [userDetails, setUserDetails] = useState({} as SignupFormData);
  const {
    campaignId,
    surveyTemplateId,
    pricingTable,
    logout,
    ssoToken,
    ssoProvider,
    authProvider,
  } = useRegisterQueryParams();

  const { logoutAndClearData } = useUserLogin();
  const { anonymousId } = useAnonymousId();
  const { data: campaign, isLoading: isLoadingCampaign } = useRegistrationCampaign(campaignId);

  const resetUserDetailsForm = () => {
    setIsEmailVerificationPending(false);
    setShouldShowEmailForm(true);
  };

  const selectEmailMethod = () => {
    setShouldShowEmailForm(true);
    signupMethod.set('email');
  };

  // effect to handle "?logout=true" query param and clean previous user data
  useEffect(() => {
    if (logout) {
      const urlWithoutLogoutParam = clearParameter(window.location.href, 'logout');
      if (window.history.replaceState) {
        window.history.replaceState(null, '', urlWithoutLogoutParam);
      }

      logoutAndClearData(() => {
        clearUserData();
      }, true);
    }
    // ignoring as this only needs to run once on component load, and does not need to render on dependency value change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // create user from sso data - when user selects register with google on the hotjar.com -
  // he lands on the insights.hotjar.com with sso_token query params
  useEffect(() => {
    if (ssoToken || ssoProvider || authProvider) {
      const userRegistrationData = {
        ssoProvider: ssoProvider || authProvider,
        ssoToken,
        terms: 'true',
        campaignHash: campaign?.hash || false,
        plan: campaign?.details?.plan || DEFAULT_PLAN,
        pricingTable: Number(pricingTable) || DEFAULT_PRICING_TABLE_NUMBER,
        persistentAnonymousId: anonymousId as string,
        locale: currentLocale,
      };
      createUser(userRegistrationData).then(() => {
        trackOnDefaultSSOSubmit();
        browserHistory.replace(
          getRoute(ONBOARDING.path, { ...stateParamsObject, signupMethod: DEFAULT_SSO_PROVIDER }),
        );
      });
    }
    // Only need this run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (signupMethod.method === '') {
      setShouldShowEmailForm(false);
    }
  }, [signupMethod.method]);

  useEffect(() => {
    if (!isLoadingCampaign && campaign && !campaign.valid) {
      setIsCampaignValid(false);
    }
    // we need to catch the point when campaign gets loaded, so subscribing to isLoadingCampaign only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCampaign]);

  const onFormSubmit = (formData: SignupFormData) => {
    setStepOneSubmitInProgress(true);
    setUserDetails(formData);
    mark(MARKS.SIGN_UP_PERSONAL_DETAILS_SUBMISSION_START);

    const verifyEmailMetadata = {
      name: formData.name,
      uuid: anonymousId as string,
      campaign: campaignId,
      surveyTemplateId,
      verifyWithCode: false,
    };

    if (isCaptchaVisible) {
      setStepOneSubmitInProgress(false);
      if (captchaValue) {
        setIsEmailVerificationPending(true);
        setShouldShowEmailForm(false);
        return Promise.resolve({ vid: vidValue });
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ reasons: [] });
    }

    return verifyEmail({
      email: formData.email,
      action: 'signup',
      verifyEmailMetadata,
      locale: currentLocale,
    }).then(
      (response) => {
        // Perf: Measure how long it took to complete the first step of sign up
        measureAndLog({
          name: MARKS.SIGN_UP_PERSONAL_DETAILS_SUBMISSION_TIME,
          startMark: MARKS.SIGN_UP_PERSONAL_DETAILS_SUBMISSION_START,
        });

        trackOnEmailFormSubmit();

        const { vid, needs_recaptcha: captchaRequired } =
          (response as AxiosResponse<VerifyEmailSuccessResponseDto>).data ?? {};

        setVidValue(vid);
        setUserDetails(formData);

        if (captchaRequired) {
          resetUserDetailsForm();
          setStepOneSubmitInProgress(false);
          throw new Error('captcha required');
        } else {
          setIsEmailVerificationPending(true);
          setShouldShowEmailForm(false);
        }
        setStepOneSubmitInProgress(false);
        return { vid };
      },
      (error) => {
        let reasons = ['unhandled'];
        const errorReasons = error?.response?.data?.reasons || [];
        if (errorReasons.length) {
          reasons = errorReasons;
        }

        setStepOneSubmitInProgress(false);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ reasons });
      },
    );
  };

  return {
    vidValue,
    isFormLoaded: !isLoadingCampaign,
    isCampaignValid,
    isStepOneSubmitInProgress,
    userDetails,
    resetUserDetailsForm,
    selectEmailMethod,
    shouldShowEmailForm,
    isEmailVerificationPending,
    onFormSubmit,
  };
};
